import {useCallback, useEffect, useRef, useState} from 'react'
import {mezclarPendientes, type DecisionCola} from '@/lib/bateria-cola'
import {guardarPendientes, leerPendientes, olvidarPendientes} from '@/lib/cola-pendientes'
import type {DecisionesAplicadas, useAplicarDecisiones} from '@/hooks/bateria/use-bateria-cola'

/** Suficiente para que una ráfaga de teclas viaje en un solo POST. */
const DEBOUNCE_MS = 600
/** Un POST en vuelo es lo normal: solo se avisa pasados ~5 debounces. El fallo real (`falloEnvio`) avisa ya. */
const DEMORA_AVISO_MS = 3000
/** Sube despacio porque la caída típica es el wifi de la sala; más allá de 15 s hay que mirar la conexión. */
const REINTENTOS_MS = [2000, 5000, 15000]

/** Un `localStorage` bloqueado por política del navegador lanza al tocarlo, no al usarlo. */
function almacenLocal(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export type EnvioDecisiones = {
  porSincronizar: number
  /** Señal y no texto: el error traducido del API pediría recargar, justo lo que no hay que hacer. */
  falloEnvio: boolean
  /** Segundos que faltan para el próximo reintento automático, o `null` si no hay ninguno agendado. */
  reintentoEn: number | null
  reintentoAgotado: boolean
  envioDemorado: boolean
  /** Decisiones de una sesión anterior que se están reenviando. */
  recuperando: number
  encolar: (nuevas: DecisionCola[]) => void
  reintentarAhora: () => void
}

/** Lo pendiente se copia a `localStorage` (`lib/cola-pendientes.ts`) hasta que el servidor lo confirma, así que
 * sobrevive a cerrar la pestaña. */
export function useEnvioDecisiones(
  aplicarDecisiones: ReturnType<typeof useAplicarDecisiones>['mutate'],
  alAplicar: (envio: DecisionCola[], conflictos: DecisionesAplicadas['conflictos']) => void,
  /** `null` mientras la ruta no conoce la evaluación. */
  clave: string | null
): EnvioDecisiones {
  const [porSincronizar, setPorSincronizar] = useState(0)
  const [falloEnvio, setFalloEnvio] = useState(false)
  const [reintentoEn, setReintentoEn] = useState<number | null>(null)
  const [reintentoAgotado, setReintentoAgotado] = useState(false)
  const [envioDemorado, setEnvioDemorado] = useState(false)
  const [recuperando, setRecuperando] = useState(0)

  const pendientes = useRef<DecisionCola[]>([])
  const enviando = useRef(false)
  const temporizador = useRef<number | null>(null)
  const fallosSeguidos = useRef(0)
  // Por ref para que `enviar` sea estable: el reintento se llama a sí mismo desde `onError` y el
  // callback de la pantalla cambia en cada render.
  const enviarRef = useRef<() => void>(() => {})
  const alAplicarRef = useRef(alAplicar)
  useEffect(() => {
    alAplicarRef.current = alAplicar
  }, [alAplicar])

  // Por ref: `enviar` debe seguir estable y la clave llega un render después.
  const claveRef = useRef(clave)
  useEffect(() => {
    claveRef.current = clave
  }, [clave])

  const enviar = useCallback(() => {
    if (pendientes.current.length === 0) return
    // Un POST en vuelo no se atropella, pero el turno se reprograma para no cortar la escalera.
    if (enviando.current) {
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      temporizador.current = window.setTimeout(() => enviarRef.current(), DEBOUNCE_MS)
      return
    }
    const envio = pendientes.current
    enviando.current = true
    aplicarDecisiones(envio, {
      onSuccess: (data) => {
        const enviadas = new Set(envio.map((decision) => `${decision.tipo}:${decision.ref}`))
        pendientes.current = pendientes.current.filter((decision) => !enviadas.has(`${decision.tipo}:${decision.ref}`))
        setPorSincronizar(pendientes.current.length)
        setFalloEnvio(false)
        fallosSeguidos.current = 0
        setReintentoEn(null)
        setReintentoAgotado(false)
        setRecuperando(0)
        const guardado = almacenLocal()
        if (guardado && claveRef.current) olvidarPendientes(guardado, claveRef.current, envio)
        alAplicarRef.current(envio, data.conflictos)
      },
      onError: () => {
        setFalloEnvio(true)
        // Mismo temporizador que se limpia al desmontar.
        fallosSeguidos.current += 1
        const espera = REINTENTOS_MS[Math.min(fallosSeguidos.current, REINTENTOS_MS.length) - 1]
        if (temporizador.current !== null) window.clearTimeout(temporizador.current)
        temporizador.current = window.setTimeout(() => enviarRef.current(), espera)
        setReintentoEn(Math.round(espera / 1000))
        setReintentoAgotado(fallosSeguidos.current >= REINTENTOS_MS.length)
      },
      onSettled: () => {
        enviando.current = false
      },
    })
  }, [aplicarDecisiones])

  useEffect(() => {
    enviarRef.current = enviar
  }, [enviar])

  const encolar = useCallback(
    (nuevas: DecisionCola[]) => {
      pendientes.current = mezclarPendientes(pendientes.current, nuevas)
      setPorSincronizar(pendientes.current.length)
      // Se guarda antes de enviar: si lo que falla es el navegador entero, no hay un después.
      const guardado = almacenLocal()
      if (guardado && claveRef.current) guardarPendientes(guardado, claveRef.current, nuevas)
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      temporizador.current = window.setTimeout(enviar, DEBOUNCE_MS)
    },
    [enviar]
  )

  // Reenvía lo de la sesión anterior sin bloquear el teclado: es idempotente por `ref`, y lo que se
  // teclee encima gana al mezclarse por `(tipo, ref)`.
  useEffect(() => {
    const guardado = almacenLocal()
    if (!guardado || !clave) return
    const rescatadas = leerPendientes(guardado, clave)
    if (rescatadas.length === 0) return
    pendientes.current = mezclarPendientes(rescatadas, pendientes.current)
    setPorSincronizar(pendientes.current.length)
    setRecuperando(rescatadas.length)
    enviarRef.current()
  }, [clave])

  // La escalera vuelve a cero: la persona acaba de actuar sobre la red.
  const reintentarAhora = useCallback(() => {
    if (temporizador.current !== null) window.clearTimeout(temporizador.current)
    fallosSeguidos.current = 0
    setReintentoEn(null)
    setReintentoAgotado(false)
    enviar()
  }, [enviar])

  useEffect(() => {
    if (reintentoEn === null || reintentoEn <= 0) return
    const id = window.setTimeout(() => setReintentoEn(reintentoEn - 1), 1000)
    return () => window.clearTimeout(id)
  }, [reintentoEn])

  // Al desmontar, lo pendiente sale ya sin esperar el debounce; `mutate` no necesita el componente montado.
  useEffect(
    () => () => {
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      if (pendientes.current.length > 0) aplicarDecisiones(pendientes.current)
    },
    [aplicarDecisiones]
  )

  // Cerrar la pestaña o refrescar es lo único que ningún cleanup de React alcanza a interceptar.
  useEffect(() => {
    if (porSincronizar === 0) return
    function advertir(evento: BeforeUnloadEvent) {
      evento.preventDefault()
      evento.returnValue = ''
    }
    window.addEventListener('beforeunload', advertir)
    return () => window.removeEventListener('beforeunload', advertir)
  }, [porSincronizar])

  // Depende del booleano y no del conteo: cada respuesta nueva reiniciaría el reloj y una red caída
  // no avisaría nunca.
  const hayEnVuelo = porSincronizar > 0
  useEffect(() => {
    if (!hayEnVuelo) {
      setEnvioDemorado(false)
      return
    }
    const id = window.setTimeout(() => setEnvioDemorado(true), DEMORA_AVISO_MS)
    return () => window.clearTimeout(id)
  }, [hayEnVuelo])

  return {
    porSincronizar,
    falloEnvio,
    reintentoEn,
    reintentoAgotado,
    envioDemorado,
    recuperando,
    encolar,
    reintentarAhora,
  }
}
